import * as React from "react"
import {Route, Routes} from "react-router-dom";
import {ApplicationContext} from "../ApplicationContext";
import SplashPage from "../Pages/SplashPage/SplashPage";
import SimOnlyPlansPage from "../Pages/SimOnlyPlansPage/SimOnlyPlansPage";
import SimOnlyPlanDetailsPage from "../Pages/SimOnlyPlanDetailsPage/SimOnlyPlanDetailsPage";
import {SimOnlyPlan} from "../Model/Sales/SimOnlyPlan";
import SimInHandCapturePage from "../Pages/SimInHandCapturePage/SimInHandCapturePage";
import {CmsContent} from "../Model/CmsContent/CmsContent";
import SimInHandPlanSelectionPage from "../Pages/SimInHandPlanSelectionPage/SimInHandPlanSelectionPage";
import BlogListPage from "../Pages/BlogListPage/BlogListPage";
import BlogViewPage from "../Pages/BlogViewPage/BlogViewPage";
import InternalErrorPage from "../Pages/InternalErrorPage/InternalErrorPage";
import SimOnlyOtherPlansSelectionPage from "../Pages/SimOnlyOtherPlansSelectionPage/SimOnlyOtherPlansSelectionPage";
import SimOnlyOrderConfirmationPage from "../Pages/SimOnlyOrderConfirmationPage/SimOnlyOrderConfirmationPage";
import NotFoundErrorPage from "../Pages/NotFoundErrorPage/NotFoundErrorPage";
import SimInHandOrderConfirmationPage from "../Pages/SimInHandOrderConfirmationPage/SimInHandOrderConfirmationPage";
import CoverageCheckerPage from "../Pages/CoverageCheckerPage/CoverageCheckerPage";
import LogoutPage from "../Pages/LogoutPage/LogoutPage";
import TermsAndConditionsPage from "../Pages/TermsAndConditions/TermsAndConditionsPage";
import AgreementPlanPreviewPage from "../Pages/AgreementPlanPreviewPage/AgreementPlanPreviewPage";
import RafPage from "../Pages/RafPage/RafPage";
import RafPageError from "../Pages/RafPage/RafPageError";
import SimOnlyPlanOnePagePurchasePage from "../Pages/SimOnlyPlanOnePagePurchase/SimOnlyPlanOnePagePurchasePage";
import ShortcodeSearchPage from "../Pages/ShortcodeSearchPage/ShortcodeSearchPage";

/**
 * Constant defines all known routes within the Sales Portal application.
 */
export const SALES_PORTAL_ROUTES = {
    Home: (context: string): string =>
        `${context}/`,

    InternalError: (context: string): string =>
        `${context}/error`,
    NotFound: (context: string): string =>
        `${context}/not-found`,

    Login: (context: String): string =>
        `${context}/login`,

    Logout: (context: string): string =>
        `${context}/logout`,

    HybrisCallback: (context: string): string =>
        `${context}/`,

    BlogList: (context: string): string =>
        `${context}/blog`,
    BlogView: (context: string, reference: string): string =>
        `${context}/blog/${reference}`,

    Coverage: (context: string): string =>
        `${context}/coverage-checker`,

    Terms: (context: string): string =>
        `${context}/terms`,

    Raf: (context: string, referralCode: string): string =>
        `${context}/RAF/${referralCode}`,

    RafError: (context: string): string =>
        `${context}/RAF/error`,

    SimOnly: {
        UserUpdate: (context: string): string =>
            `${context}/`,
        Deals: (context: string): string =>
            `${context}/sim-only/deals`,
        Alternative: (context: string, planCode: string): string =>
            `${context}/sim-only/deals/${planCode}/alternative`,
        Details: (context: string, planCode: string): string =>
            `${context}/sim-only/deals/${planCode}/view`,
        Purchase: (context: string, planCode: string): string =>
            `${context}/`,
        Confirmation: (context: string): string =>
            `${context}/sim-only/confirmation`,
        PlanAgreementPreview: (context: string, planCode: string): string =>
            `${context}/sim-only/deals/${planCode}/plan-agreement-preview`,
        PurchaseSignUp: (context: string, planCode: string): string =>
            `${context}/sim-only/deals/${planCode}/purchaseSignUp`
    },

    SimInHand: {
        activate: (context: string): string =>
            `${context}/activate`,
        UserUpdate: (context: string): string =>
        //     `${context}/sim-in-hand/user/update`,
            `${context}/`,
        PlanSelection: (context: string, planCode: string) =>
            `${context}/sim-in-hand/${planCode}`,
        Purchase: (context: string, planCode: string): string =>
        //     `${context}/sim-in-hand/${planCode}/purchase`,
            `${context}/`,
        Confirmation: (context: string): string =>
            `${context}/sim-in-hand/confirmation`,
        PlanAgreementPreview: (context: string, planCode: string): string =>
            `${context}/sim-in-hand/${planCode}/plan-agreement-preview`,
        PurchaseSignUp: (context: string, planCode: string): string =>
            `${context}/sim-in-hand/deals/${planCode}/purchaseSignUp`
    },

    Port: {
        PortSelection: (context: string, planCode: string): string =>
            `${context}/`,
        PortCapture: (context: string, planCode: string): string =>
            `${context}/`,
    },

    Shortcode: (context: string): string =>
        `${context}/shortcodes-checker`,
}

interface SalesPortalRoutesProps {

    /**
     * Array of all Sales Portal content currently stored in the CMS.
     */
    readonly cmsContent: CmsContent[]

    /**
     * Array of all known SIM-only plans that could be selected by the customer.
     */
    readonly simOnlyPlans: SimOnlyPlan[]

    /**
     * Array of all the best SIM-only plans. The criteria for "best" is reseller-dependent.
     */
    readonly bestSimOnlyPlans: SimOnlyPlan[]

}

/**
 * Function will match the current browser path to an appropriate web page.
 */
const SalesPortalRoutes = (props: SalesPortalRoutesProps): JSX.Element => {

    const applicationContext = React.useContext(ApplicationContext)

    /**
     * Hard redirect to sign in page that is in digital-experience-ui.
     * Doesn't keep the old URL in browser history and does full page reload.
     * @constructor
     */
    const RedirectToSignIn = () => {
        window.location.replace("/dashboard/sign-in");
        return null;
    };

    return (
        <Routes>
            <Route path={SALES_PORTAL_ROUTES.Home(applicationContext.urlContext)} element={
                <SplashPage
                    cmsContent={props.cmsContent}
                    simOnlyPlans={props.simOnlyPlans}/>
            }/>

            <Route path={SALES_PORTAL_ROUTES.Login(applicationContext.urlContext)} element={
                /**
                 * Navigate to sign in page that moved to digital-experience-ui
                 * Previously it went to '<LoginPage cmsContent={props.cmsContent}/>'
                 */
                <RedirectToSignIn />
            }/>
            <Route path={SALES_PORTAL_ROUTES.Logout(applicationContext.urlContext)} element={
                <LogoutPage/>
            }/>

            {applicationContext.appConfig.pagesConfiguration.blogPageEnabled && <Route
                path={SALES_PORTAL_ROUTES.BlogList(applicationContext.urlContext)}
                element={<BlogListPage cmsContent={props.cmsContent}/>}
            />}
            {applicationContext.appConfig.pagesConfiguration.blogPageEnabled && <Route
                path={SALES_PORTAL_ROUTES.BlogView(applicationContext.urlContext, ":reference")}
                element={<BlogViewPage cmsContent={props.cmsContent}/>}
            />}

            <Route
                path={SALES_PORTAL_ROUTES.Coverage(applicationContext.urlContext)}
                element={<CoverageCheckerPage cmsContent={props.cmsContent}/>}
            />

            <Route path={SALES_PORTAL_ROUTES.SimOnly.Deals(applicationContext.urlContext)} element={
                <SimOnlyPlansPage cmsContent={props.cmsContent} simOnlyPlans={props.simOnlyPlans}/>
            }/>
            <Route path={SALES_PORTAL_ROUTES.SimOnly.Alternative(applicationContext.urlContext, ":planCode")}
                   element={
                       <SimOnlyOtherPlansSelectionPage cmsContent={props.cmsContent}
                                                       simOnlyPlans={props.simOnlyPlans}/>
                   }/>
            <Route
                path={SALES_PORTAL_ROUTES.SimOnly.Details(applicationContext.urlContext, ":planCode")}
                element={
                    <SimOnlyPlanDetailsPage
                        cmsContent={props.cmsContent}
                        simOnlyPlans={props.simOnlyPlans}
                    />
                }
            />
            <Route
                path={SALES_PORTAL_ROUTES.SimOnly.PurchaseSignUp(applicationContext.urlContext, ":planCode")}
                element={<SimOnlyPlanOnePagePurchasePage cmsContent={props.cmsContent} simOnlyPlans={props.simOnlyPlans}/>}
            />
            <Route
              path={SALES_PORTAL_ROUTES.SimOnly.PlanAgreementPreview(applicationContext.urlContext, ":planCode")}
              element={<AgreementPlanPreviewPage contractType="newOrder" />}
            />
            <Route
                path={SALES_PORTAL_ROUTES.SimOnly.Confirmation(applicationContext.urlContext)}
                element={<SimOnlyOrderConfirmationPage cmsContent={props.cmsContent}/>}
            />

            <Route path={SALES_PORTAL_ROUTES.SimInHand.activate(applicationContext.urlContext)} element={
                <SimInHandCapturePage cmsContent={props.cmsContent}/>
            }/>
            <Route
                path={SALES_PORTAL_ROUTES.SimInHand.PlanSelection(applicationContext.urlContext, ":planCode")}
                element={
                    <SimInHandPlanSelectionPage
                        cmsContent={props.cmsContent}
                        simOnlyPlans={props.simOnlyPlans}
                    />
                }
            />
            <Route
              path={SALES_PORTAL_ROUTES.SimInHand.PlanAgreementPreview(applicationContext.urlContext, ":planCode")}
              element={<AgreementPlanPreviewPage contractType="newOrder" />}
            />
            <Route
                path={SALES_PORTAL_ROUTES.SimInHand.Confirmation(applicationContext.urlContext)}
                element={<SimInHandOrderConfirmationPage cmsContent={props.cmsContent}/>}
            />
            <Route
                path={SALES_PORTAL_ROUTES.SimInHand.PurchaseSignUp(applicationContext.urlContext, ":planCode")}
                element={<SimOnlyPlanOnePagePurchasePage cmsContent={props.cmsContent} simOnlyPlans={props.simOnlyPlans}/>}
            />

            <Route path={SALES_PORTAL_ROUTES.InternalError(applicationContext.urlContext)} element={
                <InternalErrorPage cmsContent={props.cmsContent}/>
            }/>
            <Route path={SALES_PORTAL_ROUTES.Terms(applicationContext.urlContext)} element={
                <TermsAndConditionsPage cmsContent={props.cmsContent}/>
            }/>
            <Route path={SALES_PORTAL_ROUTES.NotFound(applicationContext.urlContext)} element={
                <NotFoundErrorPage cmsContent={props.cmsContent}/>
            }/>
            <Route path={SALES_PORTAL_ROUTES.Raf(applicationContext.urlContext, ":referralCode")} element={
                <RafPage cmsContent={props.cmsContent}/>
            }/>
            <Route path={SALES_PORTAL_ROUTES.RafError(applicationContext.urlContext)} element={
                <RafPageError cmsContent={props.cmsContent}/>
            }/>
            <Route path={SALES_PORTAL_ROUTES.Shortcode(applicationContext.urlContext)} element={
                <ShortcodeSearchPage cmsContent={props.cmsContent}/>
            }/>
            <Route path="*" element={
                <NotFoundErrorPage cmsContent={props.cmsContent}/>
            }/>
        </Routes>
    )
}

export const addPromoQueryParam = (url: string, promoCode: string | null): string => {
    return url + (promoCode ? (url.includes("?") ? "&" : "?") + "promo=" + encodeURIComponent(promoCode) : "")
}

export const addPlanCodeQueryParam = (url: string, planCode: string): string => {
    return url + (planCode ? (url.includes("?") ? "&" : "?") + "plan=" + encodeURIComponent(planCode) : "")
}

export const addActivationCodeQueryParam = (url: string, activationCode: string | null): string => {
    return url + (activationCode ? (url.includes("?") ? "&" : "?") + "activationCode=" + encodeURIComponent(activationCode) : "")
}

export const getPromoQueryParam = ([params, _]: [URLSearchParams, any]): string | null => {
    return params.get("promo")
}

export const getActivationCodeQueryParam = ([params, _]: [URLSearchParams, any]): string | null => {
    return params.get("activationCode")
}

export default SalesPortalRoutes